import { Component, Input, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Order } from '../../../../orders/models/order.models';
import { OrderUtils } from '../../../../orders/utils/order.utils';

/**
 * Displays a summary of an order, including transaction type, pricing, date formatting, and wash count details.
 * @component
 * @selector app-order-summary-tab
 * @example
 *   <app-order-summary-tab [order]="sample_value1" [apiOrderData]="sample_value2"></app-order-summary-tab>
 * @input {Order | null} order - The order model used to derive transaction type, price, and related summary data.
 * @input {any} apiOrderData - Raw API order data used to display wash count and subscription-related information.
 * @method formatDate(dateString: string): string - Formats a date string into a readable date format, handling both standard dates and localized date-time strings.
 * @method getTransactionType(): string - Returns the transaction type for the current order, defaulting to 'Single' when no order is provided.
 * @method isMembershipTransaction(): boolean - Determines whether the current transaction is a membership transaction.
 * @method formatPrice(): string - Formats the order price using shared order utility logic.
 * @method getWashCountDisplay(): string - Builds a display string for wash count information, including unlimited subscription usage and redemption counts.
 * @method getPrepaidWashCountDisplay(): string - Total washes on a prepaid book sale and how many were redeemed.
 */
@Component({
  selector: 'app-order-summary-tab',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './order-summary-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class OrderSummaryTabComponent {
  @Input() order: Order | null = null;
  @Input() apiOrderData: any = null;

  /**
   * Formats a date string into a human-readable "day month, year" format.
   * @example
   * formatDate('Jan 5, 2024')
   * '5 January, 2024'
   * @param {string} dateString - The date string to format.
   * @returns {string} The formatted date string, or "Invalid Date" if parsing fails.
   */
  formatDate(dateString: string): string {
    if ((dateString.includes(',') && dateString.includes('PM')) || dateString.includes('AM')) {
      const datePart = dateString.split(',')[0];
      const parts = datePart.split(' ');
      if (parts.length === 3) {
        return `${parts[1]} ${parts[0]}, ${parts[2]}`;
      }
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  }

  getSummaryTransactionType(): string {
    return OrderUtils.getOrderSummaryTransactionType(this.order, this.getWashTypeRaw());
  }

  getWashTypeRaw(): string | undefined {
    return this.apiOrderData?.wash_type || this.order?.washTypeValue;
  }

  isRewashOrder(): boolean {
    return OrderUtils.isRewashWashType(this.getWashTypeRaw());
  }

  getRewashReasonDisplay(): string {
    return OrderUtils.getRewashReasonDisplay(
      this.apiOrderData?.rewash_reason,
      this.apiOrderData?.rewash_reason_display
    );
  }

  isMembershipTransaction(): boolean {
    if (this.isRewashOrder()) {
      return false;
    }
    return this.order ? OrderUtils.getTransactionType(this.order) === 'Membership' : false;
  }

  isPrepaidWashBook(): boolean {
    return OrderUtils.isPrepaidWashBookWashType(this.getWashTypeRaw());
  }

  /** "5 (2 redeemed)" for a prepaid book sale; N/A until the order detail has loaded. */
  getPrepaidWashCountDisplay(): string {
    if (!this.apiOrderData) return 'N/A';
    return OrderUtils.formatPrepaidWashCountDisplay(this.apiOrderData);
  }

  formatPrice(): string {
    return this.order ? OrderUtils.formatPrice(this.order) : '';
  }

  /**
   * CWS-1444: the plan this membership is on, and what it was sold on when
   * those differ.
   *
   * `packageName` is the CURRENT plan — a plan change moves the order with the
   * membership. The sold plan is kept separately, because reporting attributes
   * by it and a closed period must not move. Showing both means staff are never
   * left wondering which of the two they are looking at.
   *
   * @returns {string} Empty when the order carries no plan at all.
   */
  getPlanDisplay(): string {
    const current = (this.order?.packageName || '').trim();
    const soldAs = (this.order?.soldPackageName || '').trim();

    if (this.order?.planChanged && soldAs) {
      return current ? `${current} (changed from ${soldAs})` : soldAs;
    }
    return current || soldAs || '';
  }

  /**
   * Returns a formatted wash count display string for the current order.
   * @example
   * etWashCountDisplay()
   * "Unlimited (3 redeemed)"
   * @returns {string} Formatted wash count display or "N/A" when unavailable.
   **/
  getWashCountDisplay(): string {
    if (!this.apiOrderData) return 'N/A';
    return OrderUtils.formatMembershipWashCountDisplay(this.apiOrderData);
  }
}
